/**
 * sanitizer.ts — PII and secret scrubbing for RuntimeContext.
 *
 * Deep-clones the context, applies SANITIZATION_PATTERNS to all string fields,
 * and enforces MAX_PAYLOAD_SIZE by proportionally trimming buffers.
 */

import {
  RuntimeContext,
  SanitizedRuntimeContext,
  SANITIZATION_PATTERNS,
  MAX_PAYLOAD_SIZE,
} from '../context/types';

// ---------------------------------------------------------------------------
// String sanitization
// ---------------------------------------------------------------------------

/**
 * Sanitize a single string:
 *  1. Private keys (0x + 64+ hex) → [REDACTED_KEY]
 *  2. Bearer tokens → [REDACTED_TOKEN]
 *  3. API key patterns → [REDACTED_API_KEY]
 *  4. Eth addresses (0x + exactly 40 hex) → 0x1234...abcd
 *
 * Order matters: privateKey must run before ethAddress because a private key
 * is also matched by the ethAddress pattern (subset of chars).
 */
function sanitizeString(value: string): string {
  // 1. Private keys first (64+ hex chars after 0x) → [REDACTED_KEY]
  let result = value.replace(SANITIZATION_PATTERNS.privateKey, '[REDACTED_KEY]');

  // 2. Bearer tokens
  result = result.replace(SANITIZATION_PATTERNS.bearerToken, '[REDACTED_TOKEN]');

  // 3. API key patterns
  result = result.replace(SANITIZATION_PATTERNS.apiKey, '[REDACTED_API_KEY]');

  // 4. Ethereum addresses (exactly 40 hex chars — private keys already redacted)
  result = result.replace(SANITIZATION_PATTERNS.ethAddress, (match: string) => {
    // Format: 0x + first 4 hex + ... + last 4 hex
    // match = "0x" + 40 hex chars
    const hex = match.slice(2); // strip "0x"
    return `0x${hex.slice(0, 4)}...${hex.slice(-4)}`;
  });

  return result;
}

// ---------------------------------------------------------------------------
// Deep clone + sanitize
// ---------------------------------------------------------------------------

/**
 * Recursively deep-clone and sanitize all string values in a value tree.
 */
function deepSanitize<T>(value: T): T {
  if (typeof value === 'string') {
    return sanitizeString(value) as unknown as T;
  }

  if (Array.isArray(value)) {
    return value.map((item) => deepSanitize(item)) as unknown as T;
  }

  if (value !== null && typeof value === 'object') {
    const cloned: Record<string, unknown> = {};
    for (const key of Object.keys(value as object)) {
      cloned[key] = deepSanitize((value as Record<string, unknown>)[key]);
    }
    return cloned as unknown as T;
  }

  // Primitives (number, boolean, null, undefined) — return as-is
  return value;
}

// ---------------------------------------------------------------------------
// MAX_PAYLOAD_SIZE enforcement
// ---------------------------------------------------------------------------

/**
 * The names of array buffers in RuntimeContext that can be trimmed.
 */
const TRIMMABLE_BUFFERS = [
  'consoleLogs',
  'networkRequests',
  'wsEvents',
  'errors',
  'performance',
] as const;

type TrimmableBuffer = (typeof TRIMMABLE_BUFFERS)[number];

/**
 * Trim buffers proportionally until the serialized context fits within MAX_PAYLOAD_SIZE.
 * Oldest entries (start of array) are dropped first.
 */
function enforcePayloadSize(context: RuntimeContext): RuntimeContext {
  const serialized = JSON.stringify(context);
  if (serialized.length <= MAX_PAYLOAD_SIZE) {
    return context;
  }

  // Work on a mutable copy
  const mutable = {
    ...context,
    consoleLogs: [...context.consoleLogs],
    networkRequests: [...context.networkRequests],
    wsEvents: [...context.wsEvents],
    errors: [...context.errors],
    performance: [...context.performance],
  };

  // Iteratively drop one entry from the largest buffer until it fits
  while (JSON.stringify(mutable).length > MAX_PAYLOAD_SIZE) {
    // Find the buffer with the most entries
    let largest: TrimmableBuffer = 'consoleLogs';
    for (const key of TRIMMABLE_BUFFERS) {
      if (mutable[key].length > mutable[largest].length) {
        largest = key;
      }
    }

    // If all buffers are empty, we can't trim further
    if (mutable[largest].length === 0) break;

    // Drop the oldest entry (index 0)
    (mutable[largest] as unknown[]).shift();
  }

  return mutable;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Sanitize a RuntimeContext:
 * 1. Deep-clone (no mutation of original)
 * 2. Scrub PII and secrets from all string fields
 * 3. Enforce MAX_PAYLOAD_SIZE by trimming buffers
 */
export function sanitizeContext(context: RuntimeContext): SanitizedRuntimeContext {
  // Step 1 & 2: deep clone + sanitize
  const sanitized = deepSanitize(context);

  // Step 3: enforce payload size
  return enforcePayloadSize(sanitized);
}
